import React, { createContext, useContext, useState, useEffect } from "react";
import {
  TradeEntry,
  TimeFilter,
  DateRange,
  TradeStats,
  ViewMode,
  TradingRules,
  BehaviorEntry,
  BehaviorCorrelation,
  BehaviorRecommendation,
} from "../types/tradingJournal";
import {
  loadTradeEntries,
  addTradeEntry as apiAddTradeEntry, // Rename imported functions to avoid conflicts with context methods
  updateTradeEntry as apiUpdateTradeEntry,
  deleteTradeEntry as apiDeleteTradeEntry,
  getTradeStats,
  exportTradeEntriesCsv,
  loadTradingRules,
  addTradingRule as apiAddTradingRule,
  updateTradingRule as apiUpdateTradingRule,
  deleteTradingRule as apiDeleteTradingRule,
  loadBehaviorEntries,
  addBehaviorEntry as apiAddBehaviorEntry,
  updateBehaviorEntry as apiUpdateBehaviorEntry,
  deleteBehaviorEntry as apiDeleteBehaviorEntry,
  getBehaviorCorrelations,
  getBehaviorRecommendations,
  exportBehaviorEntriesCsv,
} from "../services/apicall";

import { toast } from "../hooks/use-toast";
import Cookies from "js-cookie";

const getAuthHeaders = () => {
  const token = Cookies.get("token");
  if (!token) {
    // Handle case where token is missing (e.g., redirect to login)
    console.error("Authentication token not found.");
    // You might want to redirect the user here or throw an error
    // window.location.href = "/login"; // Example redirect
    return {}; // Return empty headers or throw
  }
  return { Authorization: `Bearer ${token}` };
};

interface TradeJournalContextType {
  entries: TradeEntry[];
  filteredEntries: TradeEntry[];
  stats: TradeStats;
  timeFilter: TimeFilter;
  viewMode: ViewMode;
  customDateRange: DateRange | undefined;
  isLoading: boolean;
  editingEntry: TradeEntry | null;
  tradingRules: TradingRules[];
  behaviorEntries: BehaviorEntry[];
  filteredBehaviorEntries: BehaviorEntry[];
  behaviorCorrelations: BehaviorCorrelation[];
  behaviorRecommendations: BehaviorRecommendation[];

  // Actions
  setTimeFilter: (filter: TimeFilter) => void;
  setViewMode: (mode: ViewMode) => void;
  setCustomDateRange: (range: DateRange) => void;
  addTradeEntry: (
    entry: Omit<TradeEntry, "id" | "createdAt" | "updatedAt">
  ) => Promise<void>;
  updateTradeEntry: (id: string, entry: Partial<TradeEntry>) => Promise<void>;
  deleteTradeEntry: (id: string) => Promise<void>;
  exportEntries: () => Promise<void>;
  setEditingEntry: (entry: TradeEntry | null) => void;

  // Trading Rules
  addTradingRules: (rule: Omit<TradingRules, "id">) => Promise<void>;
  updateTradingRules: (
    id: string,
    rule: Partial<TradingRules>
  ) => Promise<void>;
  deleteTradingRules: (id: string) => Promise<void>;

  // Behavior Journal
  addBehaviorEntry: (
    entry: Omit<BehaviorEntry, "id" | "createdAt" | "updatedAt">
  ) => Promise<void>;
  updateBehaviorEntry: (
    id: string,
    entry: Partial<BehaviorEntry>
  ) => Promise<void>;
  deleteBehaviorEntry: (id: string) => Promise<void>;
  exportBehaviorEntries: () => Promise<void>;
}

const TradeJournalContext = createContext<TradeJournalContextType | undefined>(
  undefined
);

export const TradeJournalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [entries, setEntries] = useState<TradeEntry[]>([]);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("month");
  const [viewMode, setViewMode] = useState<ViewMode>("behavior");
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(true);
  const [filteredEntries, setFilteredEntries] = useState<TradeEntry[]>([]);
  const [stats, setStats] = useState<TradeStats>({} as TradeStats);
  const [editingEntry, setEditingEntry] = useState<TradeEntry | null>(null);
  const [tradingRules, setTradingRules] = useState<TradingRules[]>([]);
  const [behaviorEntries, setBehaviorEntries] = useState<BehaviorEntry[]>([]);
  const [filteredBehaviorEntries, setFilteredBehaviorEntries] = useState<
    BehaviorEntry[]
  >([]);
  const [behaviorCorrelations, setBehaviorCorrelations] = useState<
    BehaviorCorrelation[]
  >([]);
  const [behaviorRecommendations, setBehaviorRecommendations] = useState<
    BehaviorRecommendation[]
  >([]);

  const loadAllData = async (
    currentFilter: TimeFilter,
    currentCustomRange?: DateRange
  ) => {
    setIsLoading(true);
    try {
      const allTradeEntries = await loadTradeEntries("all");
      setEntries(allTradeEntries);

      const allBehaviorEntries = await loadBehaviorEntries("all");
      setBehaviorEntries(allBehaviorEntries);

      const loadedRules = await loadTradingRules();
      setTradingRules(loadedRules);

      const filteredTrades = await loadTradeEntries(
        currentFilter,
        currentCustomRange
      );
      setFilteredEntries(filteredTrades);

      const calculatedStats = await getTradeStats(
        currentFilter,
        currentCustomRange
      );
      setStats(calculatedStats);

      const filteredBehaviors = await loadBehaviorEntries(
        currentFilter,
        currentCustomRange
      );
      setFilteredBehaviorEntries(filteredBehaviors);

      const correlations = await getBehaviorCorrelations(
        currentFilter,
        currentCustomRange
      );
      setBehaviorCorrelations(correlations);

      const recommendations = await getBehaviorRecommendations(
        currentFilter,
        currentCustomRange
      );
      setBehaviorRecommendations(recommendations);
    } catch (error) {
      console.error("Error loading data:", error);
      toast({
        title: "Error",
        description: "Failed to load your trading journal data.",
        variant: "destructive",
      });
      // Decide how to handle auth errors vs network errors
      if (error?.response?.status === 401) {
        // Handle unauthorized, e.g., redirect to login
        // window.location.href = "/login";
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Initial Data Load Effect (runs once on mount)
  useEffect(() => {
    loadAllData(timeFilter, customDateRange); // Load data using initial state filter
  }, []); // Empty dependency array means this runs once on mount

  // Effect for Filter/Date Range changes
  useEffect(() => {
    // This effect should trigger a re-fetch of *filtered* data and stats/analysis
    // if timeFilter or customDateRange change.
    // The loadAllData function already does this if called with the latest filters.
    // However, we don't need to reload ALL entries/behaviors on filter change,
    // only the filtered ones and derived stats/analysis.

    // Separate function for just fetching filtered/derived data
    const fetchFilteredAndDerivedData = async () => {
      setIsLoading(true); // Or a separate 'isFiltering' loading state
      try {
        // Fetch filtered entries and stats
        const filteredTrades = await loadTradeEntries(
          timeFilter,
          customDateRange
        );
        setFilteredEntries(filteredTrades);

        const calculatedStats = await getTradeStats(
          timeFilter,
          customDateRange
        );
        setStats(calculatedStats);

        // Fetch filtered behavior entries, correlations, and recommendations
        const filteredBehaviors = await loadBehaviorEntries(
          timeFilter,
          customDateRange
        );
        setFilteredBehaviorEntries(filteredBehaviors);

        const correlations = await getBehaviorCorrelations(
          timeFilter,
          customDateRange
        );
        setBehaviorCorrelations(correlations);

        const recommendations = await getBehaviorRecommendations(
          timeFilter,
          customDateRange
        );
        setBehaviorRecommendations(recommendations);
      } catch (error) {
        console.error("Error fetching filtered data:", error);
        toast({
          title: "Error",
          description: "Failed to update journal data for selected period.",
          variant: "destructive",
        });
        if (error?.response?.status === 401) {
          /* handle auth */
        }
      } finally {
        setIsLoading(false); // Or set 'isFiltering' to false
      }
    };

    // Only run if not during the initial loading phase managed by the first effect
    // and if the component is mounted (implicit in useEffect)
    // We also need to avoid double fetching if loadAllData already ran for these filters.
    // Simplest approach: Let filter changes trigger a fetch. Initial load will set filter.
    // A small debounce could prevent rapid fetching if filters change quickly.
    if (!isLoading) {
      // Only fetch if the initial load is complete
      fetchFilteredAndDerivedData();
    }
  }, [timeFilter, customDateRange]); // Depends on filter state

  // Action functions now call API client functions and refetch data
  const addTradeEntry = async (
    entry: Omit<TradeEntry, "id" | "createdAt" | "updatedAt">
  ): Promise<void> => {
    try {
      // isLoading(true); // Optional: Show loading for this action
      const newEntry = await apiAddTradeEntry(entry); // Call API
      // setEntries([...entries, newEntry]); // Optimistic update, or refetch
      await loadAllData(timeFilter, customDateRange); // Refetch all data after successful add
      toast({
        title: "Success",
        description: "Trade entry added successfully!",
      });
    } catch (error) {
      console.error("Error adding entry:", error);
      toast({
        title: "Error",
        description: "Failed to add trade entry.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional: Hide loading
    }
  };

  const updateTradeEntry = async (
    id: string,
    entry: Partial<TradeEntry>
  ): Promise<void> => {
    try {
      // isLoading(true); // Optional
      const updated = await apiUpdateTradeEntry(id, entry); // Call API
      if (updated) {
        // setEntries(entries.map((e) => (e.id === id ? updated : e))); // Optimistic update, or refetch
        await loadAllData(timeFilter, customDateRange); // Refetch all data after successful update
        toast({
          title: "Success",
          description: "Trade entry updated successfully!",
        });
      } else {
        toast({
          title: "Error",
          description: "Trade entry not found.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating entry:", error);
      toast({
        title: "Error",
        description: "Failed to update trade entry.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional
    }
  };

  const deleteTradeEntry = async (id: string): Promise<void> => {
    try {
      // isLoading(true); // Optional
      const result = await apiDeleteTradeEntry(id); // Call API
      if (result) {
        // setEntries(entries.filter((e) => e.id !== id)); // Optimistic update, or refetch
        await loadAllData(timeFilter, customDateRange); // Refetch all data after successful delete
        toast({
          title: "Success",
          description: "Trade entry deleted successfully!",
        });
      } else {
        toast({
          title: "Error",
          description: "Trade entry not found or already deleted.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast({
        title: "Error",
        description: "Failed to delete trade entry.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional
    }
  };

  const exportEntries = async (): Promise<void> => {
    try {
      // isLoading(true); // Optional
      // apiExportTradeEntriesCsv handles the download directly
      await exportTradeEntriesCsv(timeFilter, customDateRange);
      toast({
        title: "Success",
        description: "Trading journal exported successfully!",
      });
    } catch (error) {
      console.error("Error exporting trade entries:", error);
      toast({
        title: "Error",
        description: "Failed to export trade entries.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional
    }
  };

  const addTradingRules = async (
    rule: Omit<TradingRules, "id">
  ): Promise<void> => {
    try {
      // isLoading(true); // Optional
      const newRule = await apiAddTradingRule(rule); // Call API
      // setTradingRules([...tradingRules, newRule]); // Optimistic update, or refetch
      const updatedRules = await loadTradingRules(); // Refetch rules
      setTradingRules(updatedRules);
      toast({
        title: "Success",
        description: "Strategy rules added successfully!",
      });
    } catch (error) {
      console.error("Error adding trading rule:", error);
      toast({
        title: "Error",
        description: "Failed to add strategy rules.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional
    }
  };

  const updateTradingRules = async (
    id: string,
    rule: Partial<TradingRules>
  ): Promise<void> => {
    try {
      // isLoading(true); // Optional
      const updated = await apiUpdateTradingRule(id, rule); // Call API
      if (updated) {
        // setTradingRules(tradingRules.map((r) => (r.id === id ? updated : r))); // Optimistic update, or refetch
        const updatedRules = await loadTradingRules(); // Refetch rules
        setTradingRules(updatedRules);
        toast({
          title: "Success",
          description: "Strategy rules updated successfully!",
        });
      } else {
        toast({
          title: "Error",
          description: "Strategy rule not found.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating trading rule:", error);
      toast({
        title: "Error",
        description: "Failed to update strategy rules.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional
    }
  };

  const deleteTradingRules = async (id: string): Promise<void> => {
    try {
      // isLoading(true); // Optional
      const result = await apiDeleteTradingRule(id); // Call API
      if (result) {
        // setTradingRules(tradingRules.filter((r) => r.id !== id)); // Optimistic update, or refetch
        const updatedRules = await loadTradingRules(); // Refetch rules
        setTradingRules(updatedRules);
        toast({
          title: "Success",
          description: "Strategy rules deleted successfully!",
        });
      } else {
        toast({
          title: "Error",
          description: "Strategy rule not found or already deleted.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting trading rule:", error);
      toast({
        title: "Error",
        description: "Failed to delete strategy rules.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional
    }
  };

  const addBehaviorEntry = async (
    entry: Omit<BehaviorEntry, "id" | "createdAt" | "updatedAt">
  ): Promise<void> => {
    try {
      // isLoading(true); // Optional
      const newEntry = await apiAddBehaviorEntry(entry); // Call API (backend handles upsert logic)
      // setBehaviorEntries([...behaviorEntries, newEntry]); // Optimistic or refetch
      // Refetch all behavior data and dependent calculations
      await loadAllData(timeFilter, customDateRange);
      toast({
        title: "Success",
        description: "Behavior journal entry added/updated successfully!",
      });
    } catch (error) {
      console.error("Error adding behavior entry:", error);
      toast({
        title: "Error",
        description: "Failed to add behavior journal entry.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional
    }
  };

  const updateBehaviorEntry = async (
    id: string,
    entry: Partial<BehaviorEntry>
  ): Promise<void> => {
    try {
      // isLoading(true); // Optional
      const updated = await apiUpdateBehaviorEntry(id, entry); // Call API
      if (updated) {
        // setBehaviorEntries(behaviorEntries.map((e) => (e.id === id ? updated : e))); // Optimistic or refetch
        // Refetch all behavior data and dependent calculations
        await loadAllData(timeFilter, customDateRange);
        toast({
          title: "Success",
          description: "Behavior journal entry updated successfully!",
        });
      } else {
        toast({
          title: "Error",
          description: "Behavior journal entry not found.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating behavior entry:", error);
      toast({
        title: "Error",
        description: "Failed to update behavior journal entry.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional
    }
  };

  const deleteBehaviorEntry = async (id: string): Promise<void> => {
    try {
      // isLoading(true); // Optional
      const result = await apiDeleteBehaviorEntry(id); // Call API
      if (result) {
        // setBehaviorEntries(behaviorEntries.filter((e) => e.id !== id)); // Optimistic or refetch
        // Refetch all behavior data and dependent calculations
        await loadAllData(timeFilter, customDateRange);
        toast({
          title: "Success",
          description: "Behavior journal entry deleted successfully!",
        });
      } else {
        toast({
          title: "Error",
          description: "Behavior journal entry not found or already deleted.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting behavior entry:", error);
      toast({
        title: "Error",
        description: "Failed to delete behavior journal entry.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional
    }
  };

  const exportBehaviorEntries = async (): Promise<void> => {
    try {
      // isLoading(true); // Optional
      // apiExportBehaviorEntriesCsv handles the download directly
      await exportBehaviorEntriesCsv(timeFilter, customDateRange);
      toast({
        title: "Success",
        description: "Behavior journal exported successfully!",
      });
    } catch (error) {
      console.error("Error exporting behavior entries:", error);
      toast({
        title: "Error",
        description: "Failed to export behavior journal.",
        variant: "destructive",
      });
      if (error?.response?.status === 401) {
        /* handle auth */
      }
    } finally {
      // isLoading(false); // Optional
    }
  };

  // The 'value' object exposes the same state and action function names
  const value = {
    entries,
    filteredEntries,
    stats,
    timeFilter,
    viewMode,
    customDateRange,
    isLoading,
    editingEntry,
    tradingRules,
    behaviorEntries,
    filteredBehaviorEntries,
    behaviorCorrelations,
    behaviorRecommendations,

    // Exposed Actions
    setTimeFilter,
    setViewMode,
    setCustomDateRange,
    addTradeEntry, // Calls the refactored async function
    updateTradeEntry, // Calls the refactored async function
    deleteTradeEntry, // Calls the refactored async function
    exportEntries, // Calls the refactored async function
    setEditingEntry,

    addTradingRules, // Calls the refactored async function
    updateTradingRules, // Calls the refactored async function
    deleteTradingRules, // Calls the refactored async function

    addBehaviorEntry, // Calls the refactored async function
    updateBehaviorEntry, // Calls the refactored async function
    deleteBehaviorEntry, // Calls the refactored async function
    exportBehaviorEntries, // Calls the refactored async function
  };

  return (
    <TradeJournalContext.Provider value={value}>
      {children}
    </TradeJournalContext.Provider>
  );
};

export const useTradeJournal = () => {
  const context = useContext(TradeJournalContext);
  if (context === undefined) {
    throw new Error(
      "useTradeJournal must be used within a TradeJournalProvider"
    );
  }
  return context;
};
